// Course groups on the dashboard (Dew, 2026-10-18): courses sharing a `group`
// name show as ONE big card that opens to the courses inside it (e.g.
// "AI Money Factory" -> regular + Pro). Pure and SDK-free.

export type GroupItem<C> = { kind: "course"; course: C } | { kind: "group"; name: string; courses: C[] };

/** Keep course order; a group sits where its first course was. A group with a
 *  single visible course (student bought only one) shows as that plain course. */
export function groupCourses<C extends { group?: string }>(courses: C[]): GroupItem<C>[] {
  const byGroup = new Map<string, C[]>();
  for (const c of courses) {
    const g = c.group?.trim();
    if (g) byGroup.set(g, [...(byGroup.get(g) ?? []), c]);
  }
  const out: GroupItem<C>[] = [];
  const placed = new Set<string>();
  for (const c of courses) {
    const g = c.group?.trim();
    const members = g ? byGroup.get(g)! : [];
    if (!g || members.length < 2) out.push({ kind: "course", course: c });
    else if (!placed.has(g)) {
      placed.add(g);
      out.push({ kind: "group", name: g, courses: members });
    }
  }
  return out;
}

/** Move the courses in `moving` (one course, or every course of a group) so they sit just
 *  before `beforeId`, keeping their own relative order. Returns the new full order. */
export function placeBefore<C extends { id: string }>(courses: C[], moving: string[], beforeId: string): C[] {
  if (moving.includes(beforeId)) return courses;
  const set = new Set(moving);
  const rest = courses.filter((c) => !set.has(c.id));
  const moved = courses.filter((c) => set.has(c.id));
  const at = rest.findIndex((c) => c.id === beforeId);
  return at < 0 ? courses : [...rest.slice(0, at), ...moved, ...rest.slice(at)];
}
